import { lazy, Suspense } from 'react';
import { Link, Navigate, Outlet, Route, Routes, useParams } from 'react-router';
import { K } from './kabinet/atoms';
import { cabinetPath } from './routes/params';
import { коробкаПодписи } from './layout/screen';
import { useSession } from './auth/session';
import { sessionGate } from './auth/session-gate';
import { Shell } from './layout/shell';
import { AccountShell } from './layout/account-shell';
import { HomePage } from './pages/home.page';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * КАЖДЫЙ ЭКРАН — СВОЙ КУСОК СБОРКИ, А НЕ ОДИН ФАЙЛ НА ВЕСЬ КАБИНЕТ.
 *
 * ЗАЧЕМ. Владелец: «кабинет должен летать». Всё, что перечислено ниже,
 * ехало в браузер продавца ОДНИМ куском вместе с главным экраном —
 * и не потому, что нужно, а потому, что импорт стоял наверху файла.
 *
 * Разбор кода браузером пропорционален его размеру, и это не только
 * загрузка: это время до первого нажатия. Продавец, открывший список
 * кампаний, платил разбором за экран приглашения, за форму
 * восстановления пароля, за карточку кампании, которую откроет
 * через минуту, и за экран запросов, куда, может, не пойдёт вовсе.
 *
 * ЧТО ОСТАЛОСЬ ОБЩИМ КУСКОМ: каркас (`Shell`, `AccountShell`),
 * главный экран (`HomePage`) и всё, из чего он собран. Их разделять
 * нечего — с них кабинет и начинается.
 *
 * ЧТО ЭТО СТОИТ. Один короткий запрос за куском при первом переходе
 * на экран. Ожидание закрыто `Suspense` ниже — той же строкой
 * «Загружаю…», что и раньше стояла у админки.
 * ═══════════════════════════════════════════════════════════════════════
 */
const ChoosePage = lazy(() => import('./pages/choose.page').then((m) => ({ default: m.ChoosePage })));
const ConnectPage = lazy(() => import('./cabinet/connect.page').then((m) => ({ default: m.ConnectPage })));
const CabinetsPage = lazy(() => import('./cabinet/cabinets.page').then((m) => ({ default: m.CabinetsPage })));
const PlanPage = lazy(() => import('./billing/plan.page').then((m) => ({ default: m.PlanPage })));
const MembersPage = lazy(() => import('./members/members.page').then((m) => ({ default: m.MembersPage })));
const InvitePage = lazy(() => import('./members/invite.page').then((m) => ({ default: m.InvitePage })));
const JournalPage = lazy(() => import('./pages/journal.page').then((m) => ({ default: m.JournalPage })));
const SupportPage = lazy(() => import('./support/support.page').then((m) => ({ default: m.SupportPage })));
const ClustersPage = lazy(() => import('./clusters/clusters.page').then((m) => ({ default: m.ClustersPage })));
const CampaignPage = lazy(() => import('./campaign/campaign.page').then((m) => ({ default: m.CampaignPage })));
const PasswordPage = lazy(() => import('./auth/password.page').then((m) => ({ default: m.PasswordPage })));
const PwaPage = lazy(() => import('./pages/pwa.page').then((m) => ({ default: m.PwaPage })));
const IntentsPage = lazy(() => import('./pages/intents.page').then((m) => ({ default: m.IntentsPage })));
const VersionPage = lazy(() => import('./pages/version.page').then((m) => ({ default: m.VersionPage })));
/**
 * ═══════════════════════════════════════════════════════════════════════
 * ФИНАНСОВАЯ АНАЛИТИКА — СВОИМ КУСКОМ СБОРКИ, И ЭТО НЕ МЕЛОЧЬ.
 *
 * Раздел тяжёлый: пять вкладок, четыре таблицы, два окна, суточный
 * график. Положи его в общий кусок — и продавец, открывший список
 * кампаний, платил бы разбором за экран, куда зайдёт раз в неделю.
 * ═══════════════════════════════════════════════════════════════════════
 */
const FinPage = lazy(() => import('./fin/fin.page').then((m) => ({ default: m.FinPage })));
const LoginPage = lazy(() => import('./auth/login.page').then((m) => ({ default: m.LoginPage })));
const ForgotPage = lazy(() => import('./auth/forgot.page').then((m) => ({ default: m.ForgotPage })));
const RegisterPage = lazy(() => import('./auth/register.page').then((m) => ({ default: m.RegisterPage })));
const ResetPage = lazy(() => import('./auth/reset.page').then((m) => ({ default: m.ResetPage })));
const VerifyPage = lazy(() => import('./auth/verify.page').then((m) => ({ default: m.VerifyPage })));

const AdminPage = lazy(() => import('./admin/admin.page').then((m) => ({ default: m.AdminPage })));

/**
 * Маршруты кабинета.
 *
 * ВСЁ ПОД `/rk`, И В ПУТЯХ НЕТ СЛОВ `ads`, `banner`, `promo` — требование
 * 13.5 ТЗ, и оно не суеверие. Блокировщики режут по шаблонам в адресах
 * и именах файлов: `/ads/campaigns` просто не откроется, а данные при этом
 * придут — и чинить будут исправную вёрстку. В прошлой версии трекера
 * на это ушли часы, трижды.
 *
 * ПРОСТРАНСТВО И КАБИНЕТ — ПРЕФИКС МАРШРУТА, а не состояние приложения.
 * Так их невозможно потерять при переходе и невозможно показать данные
 * не того пространства (НФ-5). Заодно ссылкой можно поделиться: «посмотри
 * вот этот кабинет» — обычная просьба в переписке с помощником.
 */
export function App() {
  return (
    /* ОДИН `Suspense` НА ВСЕ МАРШРУТЫ, а не по одному на каждый: они
       сменяют друг друга, и одновременно грузится ровно один. */
    <Suspense fallback={<p className="p-4 rk-faint">Загружаю…</p>}>
    <Routes>
      <Route path="/" element={<Navigate to="/rk" replace />} />

      {/* Проверка сборки живёт вне входа: первый вопрос после неудачной
          выкатки — «какая сборка работает», и он не должен упираться
          в форму входа. */}
      <Route path="/rk/version" element={<VersionPage />} />

      {/* ТРИ ЭКРАНА, КУДА ПРИХОДЯТ ПО ССЫЛКЕ ИЗ ПИСЬМА, И ВСЕ ТРИ ВНЕ ВХОДА.
          Человек, восстанавливающий пароль, войти не может по определению;
          отправить его на форму входа значит замкнуть круг.

          АДРЕСА ЭТИХ СТРАНИЦ — ЧАСТЬ ДОГОВОРА С СЕРВЕРОМ: их вписывает
          в письма `src/auth/auth.service.ts`. До 15.08 письмо звало
          на `/reset`, маршрута с таким адресом не было, и человек получал
          «Такой страницы нет» — читается как протухшая ссылка, а не как
          отсутствующая страница. Что адреса сошлись, стережёт
          `auth/links.spec.ts`.

          Постоянный кусок пути выигрывает у `/rk/:wsId` при разборе:
          `forgot` не будет понято как имя пространства. На этом же
          с самого начала стоит `/rk/version`. */}
      {/* РЕГИСТРАЦИЯ ВНЕ ВХОДА, как и восстановление: человек, у которого
          ещё нет кабинета, войти не может по определению. Экрана этого
          не было вовсе — маршрут на сервере жил с самого начала, а завести
          учётную запись было негде, и обнаружилось это только тогда,
          когда понадобилась вторая. */}
      <Route path="/rk/register" element={<RegisterPage />} />
      <Route path="/rk/forgot" element={<ForgotPage />} />
      <Route path="/rk/reset" element={<ResetPage />} />
      <Route path="/rk/verify" element={<VerifyPage />} />

      {/* ═══════════════════════════════════════════════════════════════
          ПРИГЛАШЕНИЕ — ЧЕТВЁРТЫЙ АДРЕС ИЗ ПИСЬМА, И ОН ТОЖЕ ВНЕ ВХОДА.

          Приглашённого в сервисе может не быть вовсе: ему шлют письмо
          на адрес, а не на учётную запись. Форму входа экран рисует сам,
          не уходя с адреса, — иначе ключ приглашения теряется при
          переходе.

          До 19.08 письмо звало на `/invite` (без `/rk`), и маршрута
          не было ни с тем адресом, ни с этим: каждый приглашённый
          получал «Такой страницы нет». Сверку стережёт
          `auth/links.spec.ts`. */}
      <Route path="/rk/invite" element={<InvitePage />} />

      <Route element={<RequireSession />}>
        {/* АДМИНКА ЖИВЁТ ВНЕ ПРОСТРАНСТВА И КАБИНЕТА, а не внутри.
            Тарифы, сотрудники и условия — не свойство чьего-то кабинета;
            повесь их на `/rk/:wsId/:cabinetId/admin`, и адрес начал бы
            врать: раздел один на сервис, а путь обещает, что их много.

            Постоянный кусок пути выигрывает у `/rk/:wsId` при разборе —
            на этом же стоят `version`, `forgot` и `reset`. */}
        <Route path="/rk/admin" element={<AdminPage />} />

        <Route path="/rk" element={<ChoosePage />} />
        <Route path="/rk/:wsId" element={<ChoosePage />} />
        {/* СТАТИЧЕСКИЙ ОТРЕЗОК ВЫШЕ ПОДСТАНОВОЧНОГО. Иначе «connect»
            уедет в `:cabinetId`, разбор адреса потребует от него вида
            опознавателя и покажет отказ вместо экрана подключения. */}
        <Route path="/rk/:wsId/connect" element={<ConnectPage />} />

        {/* ═══════════════════════════════════════════════════════════════
            НАСТРОЙКИ ПРОСТРАНСТВА — БЕЗ КАБИНЕТА, И ЭТО ИСПРАВЛЕНИЕ ЗАМКА.

            19.08.2026: пароль, двухфакторка, тариф и участники стояли
            ТОЛЬКО внутри `/:cabinetId`. У человека без кабинета их
            не было вовсе, а админка требует включённой двухфакторки
            и предлагает «включить в настройках» — которых нет.
            Владелец сервиса не мог войти в собственную админку.

            Пароль — свойство учётной записи, тариф и участники —
            свойства пространства. Кабинет тут ни при чём: их один
            комплект на пространство, а кабинетов может быть пять.

            Статический отрезок выигрывает у `:cabinetId` при разборе —
            на этом же стоит `connect` строкой выше.
            ═══════════════════════════════════════════════════════════ */}
        <Route path="/rk/:wsId/settings" element={<AccountShell />}>
          <Route index element={<PasswordPage />} />
          <Route path="cabinets" element={<CabinetsPage />} />
          <Route path="plan" element={<PlanPage />} />
          <Route path="members" element={<MembersPage />} />
        </Route>
        <Route path="/rk/:wsId/:cabinetId" element={<Shell />}>
          <Route index element={<HomePage />} />
          <Route path="clusters" element={<ClustersPage />} />
          {/* ═══════════════════════════════════════════════════════
              ОБЩЕГО ЭКРАНА «ПРАВИЛА» БОЛЬШЕ НЕТ — ОТВЕТ ЗАКАЗЧИКА
              ПО П-9, 15.09.2026: «правила исключения живут только
              внутри одной кампании, на каждую кампанию могут быть свои.
              В общем старое убрать и реализовать как в дизайне».

              ПОЧЕМУ УБРАН ВЕСЬ ЭКРАН, А НЕ ТОЛЬКО ССЫЛКА НА НЕГО.
              Он показывал ДРУГИЕ правила — кабинетные, из `minus_rules`.
              Оставь мы адрес живым — человек, пришедший по старой
              ссылке, правил бы список, который больше ничего
              не исключает: почасовой прогон с 15.09 идёт по правилам
              кампании. Экран, который выглядит рабочим и ни на что
              не влияет, хуже отсутствующего.

              АДРЕС ПЕРЕСТАЁТ ОТВЕЧАТЬ, и это осознанно: прямая ссылка
              на него могла быть послана в переписке, но она вела
              к тому, чего больше нет.
              ═══════════════════════════════════════════════════════ */}
          <Route path="intents" element={<IntentsPage />} />
          {/* ═══════════════════════════════════════════════════════
              ВТОРОЙ РАЗДЕЛ СЕРВИСА — `fin`, И ЭТО КОРОТКИЙ АДРЕС
              НАРОЧНО.

              В путях нет слов `ads`, `banner`, `promo` (требование
              13.5 ТЗ) — и «finance» тоже лишнее: адрес читают люди,
              а раздел в меню назван словами. Короткий отрезок ещё
              и не спорит с `:cabinetId`: постоянная часть пути
              выигрывает у подстановочной при разборе.
              ═══════════════════════════════════════════════════ */}
          <Route path="fin" element={<FinPage />} />
          <Route path="campaigns/:id" element={<CampaignPage />} />
          {/* ТЕ ЖЕ СТРАНИЦЫ И ВНУТРИ КАБИНЕТА — ЧТОБЫ НЕ СЛОМАТЬ АДРЕСА.
              Из шапки кабинета сюда ходят, ссылки лежат в закладках
              и в переписке. Настоящее место этих страниц — уровнем выше
              (`/rk/:wsId/settings`), но ломать прежние адреса ради
              стройности значит менять одну беду на другую. */}
          <Route path="password" element={<PasswordPage />} />
          {/* ═══════════════════════════════════════════════════════════
              «КАБИНЕТЫ WILDBERRIES» — И ВНУТРИ КАБИНЕТА ТОЖЕ.

              02.09.2026 владелец: «страница кабинеты вб вообще кривая».
              Пункт меню вёл на `/rk/:wsId/settings/cabinets` — адрес
              существующий, но в ДРУГОЙ рамке: со своей шапкой «БИДРА ·
              Настройки», своим рядом вкладок и без меню кабинета.
              Человек нажимал пункт меню и оказывался в месте, где
              этого меню больше нет.

              Ровно так же уже сделаны пароль, тариф и участники: они
              живут и уровнем выше, и здесь. Довод там же — уровень
              пространства правильнее по существу, но выходить из
              кабинета ради вопроса, возникшего внутри него, человек
              не должен.
              ═══════════════════════════════════════════════════════ */}
          <Route path="cabinets" element={<CabinetsPage />} />
          {/* «Добавить на телефон»: пункт в меню профиля вёл в никуда */}
          <Route path="pwa" element={<PwaPage />} />
          {/* ТАРИФ ВНУТРИ КАБИНЕТА, рядом с паролем: туда попадают
              из ящика, и возвращаться человек хочет туда же, откуда
              пришёл. Отдельный раздел означал бы выход из кабинета
              ради вопроса, который возник внутри него. */}
          <Route path="plan" element={<PlanPage />} />
          {/* УЧАСТНИКИ ВНУТРИ КАБИНЕТА, рядом с паролем и тарифом:
              всё это про пространство, а не про рекламу, и ходят сюда
              из одного и того же места ящика. */}
          <Route path="members" element={<MembersPage />} />
          {/* ЖУРНАЛ ПРОСТРАНСТВА — рядом с участниками, а не внутри
              кампании: он про всё сразу, и внутри кампании его уже
              не найти. Вкладка в кампании остаётся: там он про одну. */}
          <Route path="journal" element={<JournalPage />} />
          {/* ПОДДЕРЖКА — РЯДОМ С ЖУРНАЛОМ И ТАРИФОМ: всё это про
              пространство, а не про рекламу, и ходят сюда из одного
              и того же места ящика. */}
          <Route path="support" element={<SupportPage />} />
          {/* ═══════════════════════════════════════════════════════
              СБОРКА И «НЕ НАЙДЕНО» — ТОЖЕ ВНУТРИ РАМКИ КАБИНЕТА.

              Так в эталоне: у обоих экранов стоит шапка кабинета,
              разделы, колокольчик и остаток тарифа. И так правильнее
              по существу: «Такой страницы нет» без единой ссылки —
              это тупик. Человек попал сюда по устаревшей ссылке
              раздела, и ему нужна не надпись, а дорога назад —
              та самая, что лежит в ящике «Разделы».

              Верхнеуровневые `/rk/version` и `*` остаются: до входа
              рамки нет вовсе, а вопрос «какая сборка работает»
              задают как раз тогда, когда войти не получается.
              Сверка кабинета нашла это расхождение числом:
              пять узлов эталона на каждом из двух экранов.
              ═══════════════════════════════════════════════════ */}
          <Route path="version" element={<VersionPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundСама />} />
    </Routes>
    </Suspense>
  );
}

/**
 * Пускает дальше только вошедших.
 *
 * ТРИ СОСТОЯНИЯ, А НЕ ДВА. Пока первый ответ сервера не получен, человек
 * не «не вошёл» — про него ещё ничего не известно. Показать в этот миг
 * форму входа значит мигать ею при каждой перезагрузке у того, кто вошёл
 * час назад, и выглядит это как «меня разлогинило».
 */
function RequireSession() {
  const { session, loading, restoreFailed } = useSession();
  const state = sessionGate({ hasSession: session !== null, loading, restoreFailed });

  if (state === 'checking') {
    return <div className="p-6 rk-text-body rk-faint">Проверяю вход…</div>;
  }
  if (state === 'unavailable') {
    return (
      <div className="p-6 rk-text-body" role="alert">
        <p>Не удалось проверить вход. Проверьте соединение и обновите страницу.</p>
        <button type="button" className="rk-button mt-4" onClick={() => window.location.reload()}>
          Обновить страницу
        </button>
      </div>
    );
  }
  if (state === 'anonymous') return <LoginPage />;
  return <Outlet />;
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * «ТАКОЙ СТРАНИЦЫ В КАБИНЕТЕ НЕТ» — ПО ЭТАЛОНУ, УЗЕЛ В УЗЕЛ.
 *
 * Экран был написан руками и разошёлся с эталоном во всём, кроме числа
 * 404: кегль числа стоял пикселями (72) вместо `--fs-display`, крупная
 * строка была `<h1>` вместо `<b>`, кнопка одна вместо двух, и слова
 * другие. Сверка этого не видела: у нашей разметки не было `data-d`,
 * и `probe/kabinet.mjs` пятнадцать секунд ждал узел, которого нет,
 * а потом клал ВЕСЬ экран строкой «ЭКРАН НЕ СОБРАЛСЯ».
 *
 * ВТОРАЯ КНОПКА — «СБОРКА», И ОНА ПО ДЕЛУ. Самая частая дорога сюда —
 * старая вкладка после выката: адрес раздела переехал, ссылка в закладке
 * осталась. Первый вопрос в этом случае — «а какая сборка у меня
 * в браузере», и отвечает на него ровно эта страница.
 *
 * ДВА МЕСТА, ОТКУДА СЮДА ПОПАДАЮТ. Внутри рамки кабинета адреса
 * известны, снаружи (до входа) — нет: там «К кампаниям» ведёт на выбор
 * кабинета, потому что никаких кампаний ещё не выбрано. Слова кнопки
 * при этом те же — они из эталона, и менять их «по обстоятельствам»
 * значит завести вторую надпись, которой никто не рисовал.
 * ═══════════════════════════════════════════════════════════════════════
 */
function NotFound() {
  const { wsId, cabinetId } = useParams();
  const вКабинете = wsId !== undefined && cabinetId !== undefined;
  const кКампаниям = вКабинете ? cabinetPath(wsId, cabinetId) : '/rk';
  const кСборке = вКабинете ? cabinetPath(wsId, cabinetId, '/version') : '/rk/version';

  return (
    <>
      <span className={K.числоНеНайдено}>404</span>
      <b className={K.заголовокНеНайдено}>Такой страницы в кабинете нет</b>
      <span className={K.поясНеНайдено}>
        Ссылка могла устареть после обновления, а кампания — быть завершена. Кампании и деньги
        на месте, потерялся только адрес.
      </span>
      <div className={K.кнопкиНеНайдено}>
        <Link to={кКампаниям} className={`${K.кнопкаККампаниям} no-underline`}>
          К кампаниям
        </Link>
        <Link to={кСборке} className={`${K.кнопкаКСборке} no-underline`}>
          Сборка
        </Link>
      </div>
    </>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ТО ЖЕ «НЕ НАЙДЕНО», НО СО СВОЕЙ РАМКОЙ — ДЛЯ АДРЕСОВ ВНЕ КАБИНЕТА.
 *
 * Внутри кабинета узел экрана и его коробку даёт `layout/shell.tsx`:
 * имя экрана стоит на РАМКЕ, а не на содержимом, и «не найдено» тут
 * ничем не особеннее биддера. Но верхнеуровневый `<Route path="*">`
 * работает и до входа, где рамки нет вовсе, — там коробку ставит
 * сам экран.
 *
 * КОРОБКА БЕРЁТСЯ ТА ЖЕ САМАЯ, а не «такая же»: `коробкаПодписи`
 * отдаёт ровно тот объект, которым рамка обставляет этот экран внутри
 * кабинета. Вторая копия чисел разошлась бы с первой на первой правке.
 *
 * `data-kab` ЗДЕСЬ СВОЙ. Правила частиц записаны как `[data-kab] .k-…`,
 * это ПОТОМОК; без области экран вышел бы столбиком голого текста.
 * ═══════════════════════════════════════════════════════════════════════
 */
function NotFoundСама() {
  return (
    <div data-kab className="min-h-full">
      <main data-d="экран-не-найдено" style={коробкаПодписи('Не найдено')}>
        <NotFound />
      </main>
    </div>
  );
}
